import React, { useState } from 'react';
import { Activity, AlertTriangle, ArrowRight, Check, Globe2, Shield, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const Step = ({ current, total }) => (
  <div className="mt-3 text-center text-[10.5px] uppercase tracking-[0.14em] text-cyan-700">
    Step {current} of {total}
  </div>
);

const OnboardingModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  if (!open) return null;

  const next = () => step < 3 ? setStep(step + 1) : onClose();

  const steps = [
    {
      icon: <AlertTriangle className="h-5 w-5 text-amber-400" />,
      title: "What you're looking at",
      sub: 'Please read before using the map.',
      body: (
        <div className="space-y-3 text-[13.5px] leading-relaxed text-cyan-100/90">
          <p>OutbreakMap is a curated map of publicly reported news signals about infectious-disease activity around the world.</p>
          <p>A pin is a news signal, not a confirmed case, not a medical diagnosis, and not an official health authority statement.</p>
          <p>We aggregate signals from independent public sources and apply editorial review. We do not replace WHO, CDC, ECDC or your local health authority.</p>
          <p className="pt-1 text-[12px] text-cyan-500">By continuing you acknowledge OutbreakMap is informational only.</p>
        </div>
      ),
      cta: 'I understand - show me the map',
    },
    {
      icon: <Activity className="h-5 w-5 text-cyan-400" />,
      title: 'See what others miss',
      sub: 'An independent signal radar for outbreak activity worldwide.',
      body: (
        <div className="space-y-3 text-[13.5px] leading-relaxed text-cyan-100/90">
          <p>Pins are signals, not confirmed outbreaks. Colors show recent activity, not danger. We never label a place safe or dangerous, only how much signal we see right now.</p>
          <div className="mt-2 rounded-lg border border-cyan-900/50 bg-cyan-500/[0.03] p-3">
            <div className="mb-2 text-[10.5px] uppercase tracking-[0.14em] text-cyan-500">How we classify signals</div>
            <ul className="space-y-1.5 text-[12.5px]">
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" /><span><b className="text-cyan-50">Official</b> - confirmed by WHO, CDC, ECDC, PAHO or ProMED.</span></li>
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /><span><b className="text-cyan-50">Emerging</b> - corroborated across multiple independent sources.</span></li>
              <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" /><span><b className="text-cyan-50">Chatter</b> - early or isolated reports. Background signal only.</span></li>
            </ul>
          </div>
        </div>
      ),
      cta: 'Continue',
    },
    {
      icon: <Globe2 className="h-5 w-5 text-cyan-400" />,
      title: 'Choose what to track',
      sub: 'Toggle diseases on or off any time.',
      body: (
        <div className="space-y-3 text-[13.5px] leading-relaxed text-cyan-100/90">
          <p>Use the tracking panel to add or remove diseases. Each disease gets its own accent color on the pins and feed.</p>
          <p>Use the layers section to toggle Official, Emerging, and Chatter signals when you want a broader or stricter view.</p>
          <p>Your selection stays saved in this browser, no account needed.</p>
        </div>
      ),
      cta: 'Continue',
    },
    {
      icon: <Shield className="h-5 w-5 text-emerald-400" />,
      title: 'Independent and community-curated',
      sub: 'A public-source snapshot of disease signals.',
      body: (
        <div className="space-y-3 text-[13.5px] leading-relaxed text-cyan-100/90">
          <p>The browser reads static public R2 JSON snapshots. It cannot start a refresh job or call a private Worker.</p>
          <div className="flex items-center gap-2 pt-1 text-[12px] text-emerald-300/90">
            <Check className="h-3.5 w-3.5" /> No tracking by default · no account required
          </div>
        </div>
      ),
      cta: 'Open the map',
    },
  ];
  const s = steps[step];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: 'radial-gradient(ellipse at center, rgba(6,20,23,0.78) 0%, rgba(2,8,10,0.96) 70%)' }}
    >
      <div
        className="fade-in w-full max-w-[480px] rounded-lg border border-cyan-900/50 bg-[#0a1d22] p-5"
        style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(34,211,238,0.06) inset' }}
      >
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#1a3d44] bg-[#11272d]">
            {s.icon}
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-cyan-50">{s.title}</div>
            <div className="mt-0.5 text-[12px] text-cyan-500">{s.sub}</div>
          </div>
          <button onClick={onClose} className="p-1 text-cyan-700 hover:text-cyan-200" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="py-2">{s.body}</div>

        <Button onClick={next} className="mt-3 h-11 w-full rounded-lg bg-cyan-400 text-[13.5px] font-semibold text-[#062126] hover:bg-cyan-300">
          {s.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>

        <Step current={step + 1} total={steps.length} />
      </div>
    </div>
  );
};

export default OnboardingModal;
